"use client";

/**
 * Inline SVG area chart — recharts SSR sorunlarını ve bundle ağırlığını
 * atlatmak için. CMS reports paneli için yeterli; tooltips, legends yok.
 *
 * Veri formatı:
 *   data = [{ date: "2026-05-12", value: 12 }, ...]
 *   width auto (viewBox 100%), height fixed.
 */
import { useMemo } from "react";

type Point = { date: string; value: number };

export function AreaChart({
  data,
  height = 180,
  stroke = "rgb(99 102 241)", // indigo-500 default
  fill = "rgb(99 102 241 / 0.12)",
  yLabel,
}: {
  data: Point[];
  height?: number;
  stroke?: string;
  fill?: string;
  yLabel?: string;
}) {
  const { path, area, ticks, max } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        path: "",
        area: "",
        ticks: [] as Array<{ x: number; label: string }>,
        max: 0,
      };
    }
    const w = 800; // viewBox width — responsive scales it
    const h = height;
    const pad = { top: 12, right: 8, bottom: 22, left: 38 };
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;
    const max = Math.max(1, ...data.map((d) => d.value));
    const step = data.length > 1 ? innerW / (data.length - 1) : 0;
    const points = data.map((d, i) => {
      const x = pad.left + i * step;
      const y = pad.top + innerH - (d.value / max) * innerH;
      return { x, y, label: d.date.slice(5) };
    });
    const path = `M ${points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L ")}`;
    const area =
      `M ${pad.left},${pad.top + innerH} ` +
      `L ${points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L ")} ` +
      `L ${(pad.left + (data.length - 1) * step).toFixed(1)},${pad.top + innerH} Z`;
    const tickEvery = Math.max(1, Math.floor(data.length / 6));
    const ticks = points
      .filter((_, i) => i % tickEvery === 0)
      .map((p) => ({ x: p.x, label: p.label }));
    return { path, area, ticks, max };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-md bg-bg-card/40 text-xs text-text-tertiary"
        style={{ height }}
      >
        Veri yok
      </div>
    );
  }

  const w = 800;
  const h = height;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-auto w-full"
      preserveAspectRatio="none"
      aria-label={yLabel}
    >
      {/* Y axis grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((p) => {
        const y = 12 + (h - 34) * (1 - p);
        return (
          <g key={p}>
            <line
              x1={38}
              y1={y}
              x2={w - 8}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.08}
              strokeWidth={1}
            />
            <text
              x={34}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fill="currentColor"
              opacity={0.4}
              className="tabular-nums"
            >
              {Math.round(max * p)}
            </text>
          </g>
        );
      })}
      <path d={area} fill={fill} />
      <path d={path} stroke={stroke} strokeWidth={1.75} fill="none" />
      {ticks.map((t, i) => (
        <text
          key={i}
          x={t.x}
          y={h - 6}
          textAnchor="middle"
          fontSize={10}
          fill="currentColor"
          opacity={0.5}
        >
          {t.label}
        </text>
      ))}
    </svg>
  );
}
